import * as _ from 'lodash';
import { Injectable, OnDestroy } from '@angular/core';
import { ReplaySubject, Subscription, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { restEndpoint } from 'src/app/config/backend';

import { BrandModel, BrandFromGetAll, ApiModel, Brand, OrganizationModel } from 'models';
import { NotificationRequestBody, NotificationRequestData } from 'models/brand/types';
import { AuthenticatedUserService, OrganizationType } from './authenticatedUser';
import { SegmentService } from './segment.service';
import { FcProductName } from 'types';

import { Brand as SimpliedBrand } from 'shared/components/brand-filters/brand-filters.component';
import { RetailerGroupRequestBody } from 'app/types/retailer-groups-types';
import { ManufacturerPrivilege } from 'app/helpers/team-user-roles';

@Injectable()
export class BrandService implements OnDestroy {
    public userBrands: Observable<BrandFromGetAll[]>;

    private userBrandsSubject: ReplaySubject<BrandFromGetAll[]>;
    private authenticatedUserSubscriber: Subscription;

    constructor(
        private brandModel: BrandModel,
        private segmentService: SegmentService,
        private authenticatedUserService: AuthenticatedUserService,
        private apiModel: ApiModel,
        private organizationModel: OrganizationModel
    ) {
        this.userBrandsSubject = new ReplaySubject<BrandFromGetAll[]>(1);
        this.userBrands = this.userBrandsSubject.asObservable();

        this.authenticatedUserSubscriber = this.authenticatedUserService.isLoggedIn.subscribe((isLoggedIn) => {
            if (isLoggedIn) {
                this.pullUserBrands();
            } else {
                this.userBrandsSubject.next([]);
            }
        });
    }

    ngOnDestroy() {
        this.authenticatedUserSubscriber.unsubscribe();
    }

    public async createBrand(brandData: BrandData): Promise<Brand> {
        const brand = await this.brandModel.addBrand(brandData);

        this.segmentService.track('New brand created', {
            brandName: brand.name,
            brandMail: brand.contacts.main.email,
            brandPhone: brand.phone,
            brandWebsite: brand.website,
            brandOrganizationId: brand.organization
        });
        await this.pullUserBrands();

        return brand;
    }

    public async deleteBrand(brandId: string): Promise<void> {
        await this.brandModel.deleteBrand(brandId);
        this.pullUserBrands();
    }

    public async updateBrand(brandId: string, brandData: EditBrandData): Promise<Brand> {
        const brand = await this.brandModel.updateBrand(brandId, brandData);
        this.pullUserBrands();
        return brand;
    }

    public getBrand(brandId: string): Observable<Brand> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}`
        });
    }

    public getBrandByShop(brandShop: string): Observable<BrandShopConfig> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getReorderEndpoint()}/brandShop/${brandShop}/data`
        });
    }

    public getAttachmentFileUrl(brandId: string, fileId: string) {
        return `${restEndpoint}/internal/brands/${brandId}/attachments/${fileId}`;
    }

    public getBrandsForManufacturerByFcProduct(fcProduct: FcProductName): Observable<SimpliedBrand[]> {
        return this.apiModel
            .makeHttpObservableRequest({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}?fcProducts=${fcProduct}`
            })
            .pipe(map((brands) => (brands as Brand[]).map(({ _id, name }) => ({ _id, name }))));
    }

    public async addBrandsToOrganizationNotification(organizationId: string, request: NotificationRequestBody) {
        await this.organizationModel.addBrandsNotifications(organizationId, request);
        this.pullUserBrands();
    }

    public async removeBrandsFromOrganizationNotification(organizationId: string, request: NotificationRequestData[]) {
        await this.organizationModel.removeBrandsNotifications(organizationId, request);
        this.pullUserBrands();
    }

    public async updateBrandsToOrganizationNotification(organizationId: string, request: NotificationRequestBody) {
        await this.organizationModel.putBrandsNotifications(organizationId, request);
        this.pullUserBrands();
    }

    public async addRetailerGroups(brandId: string, request: RetailerGroupRequestBody) {
        await this.brandModel.addRetailerGroup(brandId, request);
        this.pullUserBrands();
    }

    private async pullUserBrands() {
        const { _id: userId, organization } = this.authenticatedUserService.user.value;
        if (organization.type !== OrganizationType.Manufacturer) {
            return this.userBrandsSubject.next([]);
        }
        const brands = await this.brandModel.getBrands().toPromise();
        const organizationVisibleBrands = await this.brandModel.getOrganizationBrandsVisibilityFile();

        const availableBrands = organizationVisibleBrands[userId]
            ? _.filter(brands, ({ _id }) => _.includes(organizationVisibleBrands[userId], _id))
            : brands;

        this.userBrandsSubject.next(availableBrands);
    }
}

interface BrandData {
    name: string;
    logo: string;
    description: string;
    contacts: {
        main: {
            name: string;
            email: string;
            preferredLanguage: string;
        };
    };
    phone: string;
    website?: string;
}

interface EditBrandData {
    name: string;
    phone?: string;
    website?: string;
    contacts: {
        main: Contact;
    };
    description: string;
    gln?: string;
    logo?: any;
}

interface Contact {
    name: string;
    email: string;
    preferredLanguage: string;
}

export interface BrandShopConfig {
    name: string;
    isAllowed: boolean;
    config?: {
        brandId: string;
        filters?: { key: string }[];
        defaultSortingCriteria?: {
            sortBy: string;
            sortOrder: string;
        };
    };
}

export enum PriceLevel {
    entry = 'entry',
    midrange = 'mid-range',
    premium = 'premium',
    luxury = 'luxury'
}

export const products = [
    { label: 'BRANDS.PRODUCTS_FILTER.MARKETING_MATERIAL', value: 'MarketingMaterial' },
    { label: 'BRANDS.PRODUCTS_FILTER.PRODUCT_DATA', value: 'ProductData' },
    { label: 'BRANDS.PRODUCTS_FILTER.PREORDER_PLATFORM', value: 'Preorder' },
    { label: 'BRANDS.PRODUCTS_FILTER.ORDER_PLATFORM', value: 'Reorder' },
    { label: 'BRANDS.PRODUCTS_FILTER.SUGGESTIONS', value: 'Suggestions' },
    { label: 'BRANDS.PRODUCTS_FILTER.AUTO_RESTOCK', value: 'AutoRestock' },
    { label: 'BRANDS.PRODUCTS_FILTER.ENDLESS_AISLE', value: 'Clara' }
];
export const mainCategories = [
    { label: 'BRAND.CATEGORIES.LIST.WOMEN', value: 'women' },
    { label: 'BRAND.CATEGORIES.LIST.MEN', value: 'men' },
    { label: 'BRAND.CATEGORIES.LIST.TEEN_KID_BABY', value: 'teen-kid-baby' },
    { label: 'BRAND.CATEGORIES.LIST.FORMAL_WEAR', value: 'formal-wear' },
    { label: 'BRAND.CATEGORIES.LIST.DENIM', value: 'denim' },
    { label: 'BRAND.CATEGORIES.LIST.SHOES', value: 'shoe' },
    { label: 'BRAND.CATEGORIES.LIST.SPORT_OUTDOOR', value: 'sport-outdoor' },
    { label: 'BRAND.CATEGORIES.LIST.UNDERWEAR_SWIMWEAR', value: 'underwear-swimwear' },
    { label: 'BRAND.CATEGORIES.LIST.OUTSIZES', value: 'outsizes' },
    { label: 'BRAND.CATEGORIES.LIST.BAGS_LUGGAGE', value: 'bags-luggage' },
    { label: 'BRAND.CATEGORIES.LIST.ACCESSORIES', value: 'accessories' },
    { label: 'BRAND.CATEGORIES.LIST.HOME', value: 'home' },
    { label: 'BRAND.CATEGORIES.LIST.OTHER', value: 'other' }
];

export const priceLevels: { label: string; value: PriceLevel }[] = [
    { label: 'BRAND.PRICE_LEVEL.ENTRY_LEVEL', value: PriceLevel.entry },
    { label: 'BRAND.PRICE_LEVEL.MID_RANGE', value: PriceLevel.midrange },
    { label: 'BRAND.PRICE_LEVEL.PREMIUM', value: PriceLevel.premium },
    { label: 'BRAND.PRICE_LEVEL.LUXURY', value: PriceLevel.luxury }
];

export const connectionStatuses = [
    { label: 'BRAND.CONNECTION_STATUS.CONNECTED', value: 'connected' },
    { label: 'BRAND.CONNECTION_STATUS.UNCONNECTED', value: 'connect' },
    { label: 'BRAND.CONNECTION_STATUS.PENDING', value: 'pending' }
];

export const dataQualityLevels = [
    { label: 'BRAND.DATA_QUALITY_LEVEL.ECOMMERCE_PRO', value: 'ecommercePro' },
    { label: 'BRAND.DATA_QUALITY_LEVEL.ECOMMERCE', value: 'ecommerce' },
    { label: 'BRAND.DATA_QUALITY_LEVEL.BASIC', value: 'basic' },
    { label: 'BRAND.DATA_QUALITY_LEVEL.UNQUALIFIED', value: 'unqualified' }
];

export const sustainabilityInfo = {
    certificates: [
        { label: 'Certified B Corporation', value: 'Certified B Corporation' },
        { label: 'Certified Sustainable Economics (CSE)', value: 'Certified Sustainable Economics (CSE)' },
        { label: 'Certified climate neutral company', value: 'Certified climate neutral company' }
    ],
    memberships: [
        { label: 'Better Cotton Initiative (BCI)', value: 'Better Cotton Initiative (BCI)' },
        { label: 'Fair Wear Foundation (FWF)', value: 'Fair Wear Foundation (FWF)' },
        {
            label: 'Amfori Business Social Compliance Initiative (BSCI)',
            value: 'Amfori Business Social Compliance Initiative (BSCI)'
        },
        { label: ' Bangladesh Accord', value: ' Bangladesh Accord' },
        { label: 'Sustainable apparel coalition (SAC) ', value: 'Sustainable apparel coalition (SAC) ' },
        { label: ' Fair Labor Association (FLA)', value: ' Fair Labor Association (FLA)' },
        { label: 'Textilbündnis', value: 'Textilbündnis' },
        { label: 'Ethical trading initiative (ETI) ', value: 'Ethical trading initiative (ETI) ' }
    ]
};

export const brandSettingsRequiredPrivileges = {
    [OrganizationType.Manufacturer]: [ManufacturerPrivilege.Admin, ManufacturerPrivilege.BrandManager]
};
